import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PromoCard from './crosspromo/PromoCard';
import EmptyState from '../common/EmptyState';
import { useCrossPromotions } from '../../hooks/useCrossPromotions';

const { FiRefreshCw, FiShare2, FiArrowRight, FiArrowLeft, FiZap } = FiIcons;

const FILTERS = ['all', 'outgoing', 'incoming'];

const StatsBar = ({ outgoing, incoming }) => {
  const totalClicks = [...outgoing, ...incoming].reduce((s, p) => s + (p.clicks || 0), 0);
  const items = [
    { label: 'You Promote', value: outgoing.length },
    { label: 'Promoted By', value: incoming.length },
    { label: 'Total Clicks', value: totalClicks },
    { label: 'Network Size', value: new Set([...outgoing.map(p => p.promoted_artist_id), ...incoming.map(p => p.promoter_artist_id)]).size },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map(({ label, value }) => (
        <div key={label} className="p-5 bg-zinc-900/40 border border-zinc-800 rounded-2xl space-y-1">
          <p className="text-2xl font-black text-white">{value}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-600">{label}</p>
        </div>
      ))}
    </div>
  );
};

const CrossPromotionSection = ({ artistId, userId, isOwner }) => {
  const { outgoing, incoming, loading, error, refetch, recordClick } = useCrossPromotions(artistId);
  const [filter, setFilter] = useState('all');

  const allPromos = [
    ...outgoing.map(p => ({ ...p, _dir: 'outgoing' })),
    ...incoming.map(p => ({ ...p, _dir: 'incoming' })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const visible = filter === 'all' ? allPromos
    : filter === 'outgoing' ? allPromos.filter(p => p._dir === 'outgoing')
    : allPromos.filter(p => p._dir === 'incoming');

  const hasAny = outgoing.length + incoming.length > 0;

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-zinc-700 border-t-white rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 border-b border-zinc-900 pb-8">
        <div className="space-y-2">
          <span className="text-xs font-black tracking-widest text-purple-500 uppercase">Network</span>
          <h2 className="text-5xl font-black tracking-tighter text-white uppercase leading-none">
            Cross-Promos
          </h2>
          <p className="text-zinc-400 font-bold text-base">
            Artist collaborations &amp; mutual shoutouts
          </p>
        </div>
        <button onClick={refetch}
          className="flex items-center gap-2 px-5 py-3 border border-zinc-800 rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:border-zinc-600 hover:text-white transition-all self-start">
          <SafeIcon icon={FiRefreshCw} className="text-sm" /> Refresh
        </button>
      </div>

      {/* Stats */}
      {hasAny && <StatsBar outgoing={outgoing} incoming={incoming} />}

      {/* How it works — shown when empty or always for context */}
      {!hasAny && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: FiShare2, title: 'Shoutouts', desc: 'Feature another artist on your page to grow both audiences.' },
            { icon: FiZap, title: 'Collabs', desc: "Announce joint projects and drive fans to each other's portals." },
            { icon: FiArrowRight, title: 'Features', desc: 'Highlight guest appearances and split-release credits.' },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="p-6 bg-zinc-900/30 border border-zinc-800/50 rounded-2xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                <SafeIcon icon={icon} className="text-purple-400 text-lg" />
              </div>
              <p className="font-black text-white uppercase tracking-tight">{title}</p>
              <p className="text-zinc-500 text-sm font-bold leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter Pills */}
      {hasAny && (
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(f => {
            const count = f === 'all' ? allPromos.length : f === 'outgoing' ? outgoing.length : incoming.length;
            return (
              <button key={f} onClick={() => setFilter(f)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all ${
                  filter === f ? 'bg-white text-black' : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:border-zinc-600'
                }`}>
                {f === 'outgoing' && <SafeIcon icon={FiArrowRight} className="text-xs" />}
                {f === 'incoming' && <SafeIcon icon={FiArrowLeft} className="text-xs" />}
                {f === 'all' ? 'All' : f === 'outgoing' ? 'You Promote' : 'Promoted By'}
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${filter === f ? 'bg-black/20' : 'bg-zinc-800'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Promo Grid */}
      {visible.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {visible.map(promo => (
            <PromoCard
              key={promo.id}
              promo={promo}
              direction={promo._dir}
              onClickPromo={recordClick}
              userId={userId}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          title={filter === 'outgoing' ? 'No outgoing promos' : filter === 'incoming' ? 'No incoming promos' : 'No cross-promos yet'}
          subtitle="Cross-promotions between artists will appear here."
        />
      )}

      {/* CTA for non-owners */}
      {!isOwner && hasAny && (
        <div className="p-6 bg-zinc-900/30 border border-zinc-800 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-xs font-black tracking-widest text-zinc-600 uppercase mb-1">Discover More</p>
            <p className="text-white font-black">Explore artists in this network</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {[...outgoing.map(p => p.promoted), ...incoming.map(p => p.promoter)]
              .filter(Boolean)
              .slice(0, 3)
              .map(partner => (
                <a key={partner.id} href={`#/${partner.slug}`}
                  className="px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-full text-xs font-black text-zinc-300 hover:border-zinc-600 hover:text-white transition-all">
                  {partner.name}
                </a>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default CrossPromotionSection;